import type { Delivery } from '../interfaces/ILogistics.js';
import { orderLinesFor } from './lines.js';
import { fold } from '../utils/text.js';

export type UnsyncedOrderNotice = {
  ref: string;
  /** The rival LINE's material name, as IT spells it — the block copy leads
   *  with this (Niv ruling 2026-09-02); the ref stays in the View action. */
  itemName: string;
  /** Which block copy fits the rival's state: `orderOpen` while it is still
   *  in flight (nothing to sync yet), `order` once it arrived unsynced. */
  messageKey: 'logistics.oneOpen.order' | 'logistics.oneOpen.orderOpen';
};

/**
 * The order carrying an UNSYNCED LINE for a fold-equal name — the lookup
 * behind the one-unsynced-line rule (design 2026-08-31, amended 2026-09-14 to
 * lines: Sync is the gate, the Delivered column is not, and the gate is now
 * per material rather than per truck). The backend enforces the invariant with
 * a 409; this makes the dialog say so live, with the copy matched to WHY the
 * rival blocks (still in flight vs arrived and awaiting sync). `excludeRef`
 * keeps an edit form from conflicting with itself.
 */
export function unsyncedOrderNoticeByName(
  deliveries: Delivery[],
  name: string,
  excludeRef?: string,
): UnsyncedOrderNotice | null {
  if (fold(name.trim()) === '') return null;
  const rival = orderLinesFor(deliveries, name.trim()).find(
    ({ order, line }) => order.ref !== excludeRef && !line.isSynced,
  );
  if (!rival) return null;
  return {
    ref: rival.order.ref,
    itemName: rival.line.itemName,
    messageKey: rival.order.deliveredAt == null ? 'logistics.oneOpen.orderOpen' : 'logistics.oneOpen.order',
  };
}
